import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircleOutlined, ShoppingOutlined, HomeOutlined } from '@ant-design/icons';
import { Button, Typography, Spin, Empty } from 'antd';
import dayjs from 'dayjs';
import { useOrders } from '@hooks';

const { Title, Paragraph } = Typography;

export const OrderSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const { getOrderBySessionId } = useOrders();
  const { data: order, isLoading, isError } = getOrderBySessionId(sessionId || '');

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      pending: 'bg-yellow-principal/20 text-yellow-principal',
      paid: 'bg-green-500/20 text-green-500',
      processing: 'bg-blue-500/20 text-blue-500',
      shipped: 'bg-purple-500/20 text-purple-500',
      delivered: 'bg-green-600/20 text-green-600',
      cancelled: 'bg-red-500/20 text-red-500',
    };
    return statusColors[status.toLowerCase()] || 'bg-gray-500/20 text-gray-500';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Spin size="large" />
          <Paragraph className="font-barlow text-white text-xl mt-4">
            Loading your order...
          </Paragraph>
        </div>
      </div>
    );
  }

  if (isError || !order || !sessionId) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <Empty
            description={
              <span className="font-barlow text-white text-xl">
                Unable to load order details
              </span>
            }
          />
          <Paragraph className="font-barlow text-zinc-400 mt-4 mb-6">
            Your order may still be processing. Please check your email for confirmation.
          </Paragraph>
          <Button
            type="primary"
            size="large"
            onClick={() => navigate('/merch')}
            className="bg-yellow-principal hover:bg-yellow-600 text-black border-none font-barlow text-xl"
          >
            Return to Store
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Success Header */}
      <section className="bg-gradient-to-b from-zinc-900 via-zinc-900 to-black py-20 border-b-2 border-yellow-principal/20">
        <div className="container mx-auto px-4 text-center">
          <div className="mb-8 animate-bounce">
            <CheckCircleOutlined 
              className="text-green-500 drop-shadow-[0_0_15px_rgba(34,197,94,0.5)]" 
              style={{ fontSize: '100px' }} 
            />
          </div>
          <Title 
            level={1} 
            className="font-android !font-normal !text-5xl md:!text-6xl text-yellow-principal uppercase mb-6 drop-shadow-[0_0_30px_rgba(227,175,37,0.5)]"
          >
            PAYMENT SUCCESSFUL!
          </Title>
          <Paragraph className="font-barlow text-white text-2xl mb-4 font-bold">
            Thank you for your purchase
          </Paragraph>
        </div>
      </section>

      {/* Order Details */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="bg-zinc-900 border-2 border-zinc-700 shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-yellow-principal to-yellow-600 px-8 py-6">
              <Title level={2} className="font-android !text-white uppercase mb-0 !text-3xl">
                ORDER DETAILS
              </Title>
            </div>

            {/* Order Information Grid */}
            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-zinc-800 border border-zinc-700 p-5">
                  <Paragraph className="font-android text-yellow-principal text-sm uppercase mb-2 font-bold">
                    Order Number
                  </Paragraph>
                  <Paragraph className="font-barlow text-white text-xl mb-0 font-bold">
                    {order.orderNumber}
                  </Paragraph>
                </div>

                <div className="bg-zinc-800 border border-zinc-700 p-5">
                  <Paragraph className="font-android text-yellow-principal text-sm uppercase mb-2 font-bold">
                    Date
                  </Paragraph>
                  <Paragraph className="font-barlow text-white text-xl mb-0 font-bold">
                    {dayjs(order.createdAt).format('MMM DD, YYYY')}
                  </Paragraph>
                  <Paragraph className="font-barlow text-white text-sm mb-0">
                    {dayjs(order.createdAt).format('HH:mm')}
                  </Paragraph>
                </div>

                <div className="bg-zinc-800 border border-zinc-700 p-5">
                  <Paragraph className="font-android text-yellow-principal text-sm uppercase mb-2 font-bold">
                    Email
                  </Paragraph>
                  <Paragraph className="font-barlow text-white text-lg mb-0 break-all">
                    {order.customerEmail}
                  </Paragraph>
                </div>
              </div>

              {/* Status Badge */}
              <div className="mb-8">
                <Paragraph className="font-android text-yellow-principal text-sm uppercase mb-3 font-bold">
                  Status
                </Paragraph>
                <span 
                  className={`inline-block px-6 py-3 ${getStatusColor(order.status)} font-barlow font-bold uppercase text-lg border-2 border-current`}
                >
                  {order.status}
                </span>
              </div>

              {/* Items Table */}
              <div className="mb-8">
                <Title level={3} className="font-android text-white uppercase mb-4 !text-2xl">
                  Items Ordered
                </Title>
                <div className="bg-zinc-800 border-2 border-zinc-700 overflow-hidden">
                  {/* Table Header */}
                  <div className="grid grid-cols-12 gap-4 bg-zinc-900 px-6 py-4 border-b-2 border-yellow-principal">
                    <div className="col-span-5 font-android text-yellow-principal uppercase font-bold text-sm">
                      Product
                    </div>
                    <div className="col-span-2 font-android text-yellow-principal uppercase font-bold text-sm text-center">
                      Quantity
                    </div>
                    <div className="col-span-2 font-android text-yellow-principal uppercase font-bold text-sm text-right">
                      Price
                    </div>
                    <div className="col-span-3 font-android text-yellow-principal uppercase font-bold text-sm text-right">
                      Subtotal
                    </div>
                  </div>

                  {/* Table Body */}
                  {order.items.map((item: any, index: number) => (
                    <div 
                      key={item.id}
                      className={`grid grid-cols-12 gap-4 px-6 py-5 ${
                        index < order.items.length - 1 ? 'border-b border-zinc-700' : ''
                      } hover:bg-zinc-750 transition-colors`}
                    >
                      <div className="col-span-5 font-barlow text-white text-lg font-bold">
                        {item.productName}
                      </div>
                      <div className="col-span-2 font-barlow text-white text-lg text-center">
                        {item.quantity}
                      </div>
                      <div className="col-span-2 font-barlow text-yellow-principal font-bold text-lg text-right">
                        {formatPrice(item.price)}
                      </div>
                      <div className="col-span-3 font-barlow text-yellow-principal font-bold text-lg text-right">
                        {formatPrice(item.price * item.quantity)}
                      </div>
                    </div>
                  ))}

                  {/* Total */}
                  <div className="bg-yellow-principal px-6 py-5">
                    <div className="grid grid-cols-12 gap-4">
                      <div className="col-span-9 font-android text-black uppercase font-bold text-xl text-right">
                        TOTAL:
                      </div>
                      <div className="col-span-3 font-barlow text-black font-bold text-2xl text-right">
                        {formatPrice(order.totalAmount)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* What's Next Section */}
              <div className="bg-gradient-to-r from-zinc-800 to-zinc-850 border-2 border-yellow-principal/30 p-6 mb-8">
                <Paragraph className="font-android text-yellow-principal text-xl mb-3 font-bold uppercase">
                  What's next?
                </Paragraph>
                <Paragraph className="font-barlow text-white text-base leading-relaxed mb-0">
                  You will receive a confirmation email at{' '}
                  <span className="text-yellow-principal font-bold">{order.customerEmail}</span>{' '}
                  with your order details and tracking information.
                </Paragraph>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  type="primary"
                  size="large"
                  icon={<ShoppingOutlined />}
                  onClick={() => navigate('/merch')}
                  className="bg-yellow-principal hover:bg-yellow-600 text-black border-none font-barlow text-xl h-16 px-8 shadow-lg shadow-yellow-principal/30 hover:shadow-yellow-principal/50 transition-all"
                >
                  Continue Shopping
                </Button>
                <Button
                  size="large"
                  icon={<HomeOutlined />}
                  onClick={() => navigate('/')}
                  className="bg-zinc-700 text-white border-2 border-zinc-600 hover:bg-zinc-600 hover:border-yellow-principal font-barlow text-xl h-16 px-8 transition-all"
                >
                  Back to Home
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
